import { useOutletContext, useSubmit } from "@remix-run/react";
import { useState } from "react";
import { Close, ModalWithOverlay } from "~/components/Modal";
import { StoreLoaderContext } from "~/entities/global/types";
import BusinessConfirmation from "./BusinessConfirmation";
import BusinessProfile from "./BusinessProfile";
import BusinessSurvey from "./BusinessSurvey";
import CharitySurvey from "./CharitySurvey";
import CompleteProfile from "./CompleteProfile";
import CreateAccount from "./CreateAccount";
import MemberSurvey from "./MemberSurvey";
import SchoolSurvey from "./SchoolSurvey";
import SurveyConfirmation from "./SurveyConfirmation";

const CreateAccountWizard: React.FC = () => {
  const [step, setStep] = useState<string>("create_account");
  const context = useOutletContext<StoreLoaderContext>();
  const submit = useSubmit();

  const finishRegistration = () => {
    const formData = new FormData();
    formData.append("intent", "finish_registration");
    submit(formData, {
      method: "post",
      action: "/resources/create-account-wizard",
    });
  };

  const handleClose = () => {
    let message;
    let returnToWizard;
    switch (step) {
      case "business_survey":
      case "business_profile":
        message =
          "To get your special member pricing, complete your registration.";
        break;
      case "school_survey":
      case "charity_survey":
        message = "Help us serve you better by completing your registration.";
        break;
    }
    if (message) {
      returnToWizard = confirm(
        `${message}\nPress OK to complete your registration.`,
      );
    }
    if (!returnToWizard) {
      context.setCreateAccountWizardOpen(false);
      if (step !== "create_account") {
        finishRegistration();
      }
    }
    return;
  };

  return (
    <ModalWithOverlay isOpen={context?.createAccountWizardOpen}>
      <div
        className="w-full max-w-[500px] flex flex-col rounded-none bg-white px-2 pb-8 pt-2 font-sans shadow-lg md:border md:border-black"
        data-testid="create-account-wizard"
      >
        <Close onClick={handleClose} />

        <CreateAccount
          onSuccess={(customerType) => setStep(`${customerType}_survey`)}
          isActive={step === "create_account"}
        />

        <BusinessSurvey
          onSuccess={() => setStep("business_profile")}
          isActive={step === "business_survey"}
        />
        <MemberSurvey
          onSuccess={() => setStep("survey_confirmation")}
          isActive={step === "member_survey"}
        />
        <SchoolSurvey
          onSuccess={() => setStep("survey_confirmation")}
          isActive={step === "school_survey"}
        />
        <CharitySurvey
          onSuccess={() => setStep("survey_confirmation")}
          isActive={step === "charity_survey"}
        />

        <BusinessProfile
          onSuccess={() => setStep("business_confirmation")}
          isActive={step === "business_profile"}
        />
        <BusinessConfirmation isActive={step === "business_confirmation"} />

        <SurveyConfirmation
          onContinue={() => setStep("complete_profile")}
          isActive={step === "survey_confirmation"}
        />
        <CompleteProfile
          onSuccess={() => finishRegistration()}
          isActive={step === "complete_profile"}
        />
      </div>
    </ModalWithOverlay>
  );
};

export default CreateAccountWizard;
